import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { getValuesFromEnum } from "@app/class/Utilities";
import { UserType } from "@app/enum/UserType";
import { IUser } from "@app/interfaces/IUser";
import { CurrentUserService } from "@app/services/currentUser.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, from } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { IEngineerJob, IEngineerJobItem } from "../../foundation.interfaces";
import { VisitFailureReason } from "../../_enums/VisitFailureReason";
import { IVisitFailureRecord } from "../../_models/IVisitFailureRecord";
import { FoundationAccountService } from "../../_services/account.service.ajs";
import { EngineerService } from "../../_services/engineer.service";
import { VisitFailureService } from "../../_services/visit-failure.service";
import './visit-failure-form.component.scss';


@UntilDestroy()
@Component({
    selector: "visit-failure-form",
    template: require('./visit-failure-form.component.html')
})
export class VisitFailureFormComponent {
    @Input() jobItem: IEngineerJobItem;
    @Input() job: IEngineerJob;
    @Input() recordToEdit: IVisitFailureRecord;
    @Input() accountId: number;

    @Output() visitFailureSaved = new EventEmitter<IVisitFailureRecord>();

    editMode: boolean;
    issueForm: FormGroup;

    VisitFailureReason = VisitFailureReason;
    failReasons: number[] = getValuesFromEnum(VisitFailureReason);
    
    currentUser: IUser;
    engineers: IUser[];
    selectedEngineer: IUser;

    visitFailureRecord: IVisitFailureRecord;
    disableChargeable: boolean;

    loading: boolean;

    constructor(
        private readonly accountService: FoundationAccountService,
        private readonly currentUserService: CurrentUserService,
        private readonly engineerService: EngineerService,
        private readonly visitFailureService: VisitFailureService
    ) { }

    ngOnChanges(): void {
        this.getData();
    }

    get userIsEngineer(): boolean {
        return this.currentUser?.Type == UserType.Engineer;
    }

    get formValid(): boolean {
        return this.issueForm?.valid;
    }

    save(): void {
        if (!this.issueForm.valid) {
            return;
        }

        this.visitFailureRecord.FailureTimestampUTC = this.visitFailureRecord.FailureTimestampUTC || new Date().toUTCString();

        this.visitFailureService.saveVisitFailure(this.visitFailureRecord).pipe(
            untilDestroyed(this)
        ).subscribe({
            next: record => this.visitFailureRecord = record,
            complete: () => this.visitFailureSaved.emit(this.visitFailureRecord)
        })
    }

    engineerSearchPredicate(engineer: IUser, text: string): boolean {
        return engineer.Description?.toLowerCase().includes(text.toLowerCase());
    }

    onEngineerClicked(engineer: IUser): void {
        this.selectedEngineer = engineer;
        this.issueForm.controls.Engineer.setValue(engineer);
    }

    clearEngineer(): void {
        this.selectedEngineer = null;
        this.issueForm.controls.Engineer.setValue(undefined);
    }

    onReasonChanged(reason: VisitFailureReason): void {

        if (reason === VisitFailureReason.KitNotOnSiteCustomer ||
            reason === VisitFailureReason.VehicleNotOnSite) {
            this.issueForm.controls.Chargeable.setValue(true);
            this.disableChargeable = true;
            return;
        }

        this.issueForm.controls.Chargeable.setValue(false);
        this.disableChargeable = false;
    }

    //--

    private getData(): void {
        this.loading = true;

        combineLatest([
            from(this.currentUserService.getCurrentUser() as Promise<IUser>),
            this.engineerService.getEngineers()
        ]).pipe(
            untilDestroyed(this)
        ).subscribe({
            next: ([currentUser, engineers]) => {
                this.currentUser = currentUser;
                this.engineers = engineers;

                if (this.recordToEdit) {
                    this.setExistingRecord();
                    this.loading = false;
                    return;
                }

                const accountId = this.job?.AccountId || this.accountId;

                if (!accountId) {
                    console.error("VisitFailureFormComponent requires either a job or AccountId input");
                }

                this.visitFailureRecord = {
                    Id: 0,
                    AccountId: accountId,
                    UserId: this.currentUser.Id,
                    EngineerId: this.currentUser.Type === UserType.Engineer ? this.currentUser.Id : undefined,
                    Chargeable: false,
                    JobItemId: this.jobItem?.Id,
                    EngineerReason: "",
                    CustomerAdvisor: "",
                    VehicleId: this.jobItem?.VehicleId
                }

                this.createForm();
                this.loading = false;
            }
        })
    }

    private setExistingRecord(): void {
        this.editMode = true;
        this.visitFailureRecord = { ...this.recordToEdit };
        this.createForm();
    }


    private createForm(): void {
        if (this.issueForm) {
            return;
        }

        this.selectedEngineer = this.userIsEngineer ? this.currentUser : null;

        if (this.visitFailureRecord.EngineerId) {
            this.selectedEngineer = this.engineers.find(eng => eng.Id === this.visitFailureRecord.EngineerId);
        }

        this.issueForm = new FormGroup({
            Comment: new FormControl(this.visitFailureRecord.EngineerReason, {
                validators: [Validators.required, Validators.minLength(25)]
            }),
            Engineer: new FormControl(this.selectedEngineer, {
                validators: [Validators.required]
            }),
            Contact: new FormControl(this.visitFailureRecord.CustomerAdvisor),
            Reason: new FormControl(this.visitFailureRecord.Id ? this.visitFailureRecord.VisitFailureReason : null, {
                validators: [Validators.required]
            }),
            Chargeable: new FormControl(this.visitFailureRecord.Chargeable)
        });

        this.issueForm.valueChanges.pipe(
            debounceTime(300),
            untilDestroyed(this)
        ).subscribe(values => {
            this.visitFailureRecord = {
                ...this.visitFailureRecord,
                EngineerReason: values.Comment,
                EngineerId: values.Engineer?.Id,
                CustomerAdvisor: values.Contact,
                VisitFailureReason: values.Reason,
                Chargeable: values.Chargeable
            }
        })
    }
}
